// REQ-077 — Tab "Reconocimiento Diario": semana actual vs previa (+variación), embudo de
// participación (barras DOM) y tops de personas/fundamentales. SIN puntos.
//
// E6 — export multi-hoja del tab completo. La forma visual del libro (banner, colores, celdas
// combinadas, anchos de columna) y los textos de sus 6 hojas viven en `dailyExcel.ts`, calcados de
// la plantilla del cliente `01 - Reconocimiento Diario.xlsx`; las hojas que este reporte comparte
// con «Brillamos Juntos» salen de `excelPlantillaTeal.ts`. Este componente solo prepara los datos.
//
// El libro entero sale en el IDIOMA de quien descarga (`reports.daily.*`), en los 7 idiomas.
import { TopPersonasTable, textoCrossOu } from './TopPersonasTable';
import { DistribucionFundamental } from './DistribucionFundamental';
import { Paginacion, usePaginacion } from '../Paginacion';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useRecognitionDaily, recognitionReportsApi } from '@/api/recognition';
import type {
  RecognitionReportParams, RankedEntryDto, DailyPointDto, AdopcionDto,
} from '@/api/recognition';
import { usePersonNameResolver, useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import {
  ReportStatus, KpiRow, SuppressedNote, formatVariation, variationTone,
  withResolvedNames, filtrosActivosDe, soloPersonaDe, conFiltrosDeTira,
} from '../reportsShared';
import type { KpiItem } from '../reportsShared';
import { AvisoFiltroNoAplica } from '../AvisoFiltroNoAplica';
import { exportMultiSheetExcel } from '@/utils/exportExcelMultiSheet';
import type { ExcelCellValue, ExcelSheetSpec } from '@/utils/exportExcelMultiSheet';
import { useFundamentalsForReporting } from '@/hooks/useFundamentalsForReporting';
import { useFundamentalNaming } from '@/hooks/useFundamentalNaming';
import { fetchRecognitionsCrudoPaginado } from '../../fetchRecognitionsCrudas';
import { resolverFundamentalesConHabitos } from '../../fundamentalesHabitosMatrix';
import { buildDefinicionesSheet, buildResumenEjecutivoSheet } from './dailyExcel';
import type { KpisDiario, MetadatosDiario } from './dailyExcel';
import {
  SIN_DATO, buildEmbudoSheet, buildHojaSinDatos, buildRankingSheet,
  buildTodosLosReconocimientosSheet,
} from './excelPlantillaTeal';
import type { FilaRankingPlantilla, Traductor } from './excelPlantillaTeal';

/**
 * Porcentaje del embudo sobre la plantilla.
 *
 * 🔴 Con `toFixed(0)` un 0,05 % se imprimía «0%», y «22 personas (0%)» se lee como «no hay nadie»
 * cuando hay 22 personas reales. Salió a la vista al cerrar T-09: hasta entonces la plantilla era 0,
 * el backend mandaba `pct = null` y no se pintaba porcentaje ninguno. Un valor pequeño se muestra
 * como «<0,1 %», que dice la verdad (es poco) sin decir la mentira (es nada).
 */
export function formatPctEmbudo(pct: number): string {
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${pct < 10 ? pct.toFixed(1) : pct.toFixed(0)}%`;
}

/**
 * UAT 24-ago — porcentaje de adopción de un grupo, o `null` si no se puede calcular.
 *
 * 🔴 `plantilla === 0` significa DESCONOCIDA, no «grupo vacío»: es lo que devuelve el censo cuando no
 * hay snapshot. Dividir ahí no da 0 %, no da nada — y un «0 %» en pantalla se leería como «nadie de
 * este grupo participa», que es una afirmación que no tenemos con qué sostener.
 *
 * 🔴 QA hallazgo sobre archivo real (24-ago) — redondear a entero apachurra un % real y chico a "0%":
 * 5 nominadores de una plantilla de 23 918 son 0,02 %, no cero. Se redondea a 2 decimales, mismo
 * criterio que ya usa el Embudo del backend (`FunnelBuilder.cs`,
 * `Math.Round(numerator * 100d / denominator, 2)`) — cada consumidor de este valor (`ShineTab.tsx` y
 * este mismo archivo) formatea el resultado con `.toFixed(2)` para fijar también los ceros de sobra
 * que JS recorta (un 50 exacto se vería "50%", no "50.00%", si no se fija el string).
 */
export function pctAdopcion(dato: AdopcionDto | null | undefined): number | null {
  if (!dato || dato.plantilla <= 0) return null;
  return Math.round((dato.reconocedores / dato.plantilla) * 10000) / 100;
}

/**
 * Porcentaje de adopción de un segmento como FRACCIÓN 0-1 — el formato `0.00%` de Excel hace la
 * conversión, y así la celda sigue siendo un número ordenable en vez del texto `"50.00%"` que se
 * alineaba del lado contrario a los conteos de su misma columna.
 *
 * `null` = censo sin responder (contrato de `AdopcionDto`): cae al guion de «sin dato», nunca a un
 * 0 % que afirmaría que nadie de ese grupo reconoció.
 */
function fraccionDeAdopcion(dato: AdopcionDto | null | undefined): ExcelCellValue {
  const pct = pctAdopcion(dato);
  return pct === null ? SIN_DATO : pct / 100;
}

/** Detalle («N de M han reconocido») de una fila de adopción; vacío si el censo no respondió. */
function detalleDeAdopcion(dato: AdopcionDto | null | undefined, t: TFunction<'recognition'>): string {
  if (pctAdopcion(dato) === null) return '';
  return t('reports.daily.adoptionHint', {
    reconocedores: dato!.reconocedores,
    plantilla: dato!.plantilla,
  });
}

/**
 * De `RankedEntryDto` (lo que manda el backend) a la fila que pinta `buildRankingSheet`.
 *
 * `crossOu` sale de `textoCrossOu` —el mismo criterio que ya usa la tabla en pantalla— porque en el
 * agregado del Diario ese campo es un BOOLEANO («¿alguno cruzó de OU?»), no el número de OUs
 * distintas que trae el de campaña: un límite real del dato, no una simplificación de este export.
 */
function filasDeRanking(
  entradas: RankedEntryDto[], traducir: Traductor,
): FilaRankingPlantilla[] {
  return entradas.map((entrada): FilaRankingPlantilla => ({
    nombre: entrada.nombre,
    isSuprimido: Boolean(entrada.suprimido),
    email: entrada.email,
    employeeId: entrada.employeeId,
    area: entrada.area,
    ouName: entrada.ouName,
    site: entrada.site,
    fundamental: entrada.fundamentalName,
    count: entrada.count,
    crossOu: traducir(`crossOu.${textoCrossOu(entrada.crossOu)}`),
    tipoDominante: entrada.tipo,
  }));
}

/**
 * Suelo del ancho de una barra del embudo, en % del carril. Una etapa con gente dentro nunca se
 * dibuja como cero: con la adopción real de QA (0,05 %) su ancho proporcional es medio píxel.
 */
const ANCHO_MINIMO_ETAPA = 2;

/**
 * Paleta del embudo, tomada LITERALMENTE del prototipo (`fundamentales-admin.js`, bloque «Embudo de
 * Adopción»): verde para quien reconoce con hábito, cian para quien lo hace de vez en cuando, rosa
 * para quien no reconoce. No son los tokens de marca de la app y no se sustituyen por los más
 * parecidos: la maqueta usa estos tres y aproximarlos se nota.
 *
 * La clave es el nombre de etapa que manda el backend (Habituales/Ocasionales/Inactivos). Una etapa
 * que no reconozcamos cae al gris por omisión en vez de heredar el color de otra — pintarla del color
 * de «Habituales» diría algo que no sabemos.
 */
const COLOR_DE_ETAPA: Readonly<Record<string, string>> = {
  Habituales: '#54eda0',
  Ocasionales: '#43b7e8',
  Inactivos: '#ee5293',
};
const COLOR_ETAPA_POR_OMISION = '#94a3b8';

/**
 * Por debajo de este ancho (en % del carril) el rótulo NO cabe dentro de la barra y se pinta al lado.
 *
 * 30 % es lo que ocupa la etiqueta más larga («Inactivos · 0 recos/mes…» más el porcentaje y el
 * conteo) a la anchura habitual del panel. Con datos muy sesgados las etapas pequeñas caen al suelo
 * del 2 % y entran de lleno en este caso.
 */
const ANCHO_MINIMO_TEXTO_DENTRO = 30;

const NOMBRE_ARCHIVO = 'reporteria-diario';

/**
 * E5 — semilla del selector propio de año/mes del Embudo: arranca en el mes de `reference` (el
 * `endDate` del calendario superior) o, si no hay uno, en el mes en curso. Es solo el punto de
 * partida — a partir de ahí el selector vive en estado local y deja de seguir al calendario
 * superior, que es justo lo que pide "independiente" en el brief.
 *
 * `referenceDate` se recorta directamente con `.slice(0, 7)` en vez de pasar por `new Date(...)`:
 * un string "YYYY-MM-DD" se interpreta como medianoche UTC, pero `.getFullYear()`/`.getMonth()`
 * leen la hora LOCAL del navegador — en timezones negativos (toda LATAM) eso retrocede un mes
 * cada vez que la fecha cae el día 1. Mismo criterio ya usado para `embudoDesde`/`embudoHasta`.
 */
function seedYearMonth(referenceDate: string | undefined): string {
  if (referenceDate) {
    return referenceDate.slice(0, 7);
  }
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * `canExportData` default `true` a propósito: preserva el comportamiento de los tests existentes,
 * que montan el tab aislado sin pasar el prop. El valor REAL (permiso granular `exportData`) lo
 * calcula y pasa `RecognitionReports`, el único punto de montaje en producción — ver su comentario.
 */
export function DailyTab(
  { params, canExportData = true }: Readonly<{
    params: RecognitionReportParams;
    canExportData?: boolean;
  }>,
) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  // Filtros de la tira ACTIVOS ahora mismo, y cuáles de ellos avisan cada sección de este tab. El
  // embudo SÍ acepta Fundamental (reparte por él dentro de cada etapa), pero no una sola persona.
  const filtrosActivos = useMemo(() => filtrosActivosDe(params), [params]);
  const soloPersona = useMemo(() => soloPersonaDe(filtrosActivos), [filtrosActivos]);
  // E5 — filtro propio de año/mes del Embudo de Adopción, independiente de `params`/`reference`.
  const [funnelMonth, setFunnelMonth] = useState(() => seedYearMonth(params.endDate));
  /*
   * Tamaño de los DOS Top. Manda el `top` de la petición, no un recorte del cliente: el handler
   * devuelve exactamente estas filas (10 por omisión, techo 200). Por eso el control es de TAMAÑO
   * y no de páginas — el backend no tiene offset, así que no hay «página 2» que ofrecer.
   */
  /*
   * Filas que se PIDEN para los dos Top. Es fijo, no un control en pantalla.
   *
   * 🔴 Antes había un selector de tamaño suelto que mandaba este `top`. Con cada tabla paginando lo
   * suyo, ese tercer control quedaba huérfano debajo de las tarjetas y competía con los dos pies:
   * tres selectores, dos significados distintos. Reporte de UAT.
   *
   * El valor es el «Top 200» de las hojas «Definiciones» del cliente —el máximo que alguien tiene una
   * razón declarada para querer ver— y el handler lo clampea ahí de todos modos. Se pide una vez y
   * cada tabla pagina en cliente.
   */
  const TOP_PEDIDO = 200;
  const [funnelYear, funnelMonthNumber] = useMemo(() => {
    const [year, month] = funnelMonth.split('-').map(Number);
    return [year, month] as const;
  }, [funnelMonth]);
  const q = useRecognitionDaily({ ...params, top: TOP_PEDIDO }, { anio: funnelYear, mes: funnelMonthNumber });
  const data = q.data;
  // Punto 6 (revisión final) — `params.personaId` se suma al lote: sin esto, el «Filtros aplicados»
  // del export (más abajo) mostraría el id crudo de la persona elegida si no aparece en ningún Top.
  const personIds = useMemo(() => {
    const idsDelTop = [...(data?.topPersonas ?? []), ...(data?.topReconocedores ?? [])]
      .map((entrada) => entrada.id);
    return params.personaId ? [...idsDelTop, params.personaId] : idsDelTop;
  }, [data, params.personaId]);
  const { resolvePersonName } = usePersonNameResolver(personIds);
  const { resolveOuName } = useOuNameResolver();

  // Paridad con ShineTab.tsx — catálogo de reportería (con inactivos) para resolver nombres/colores
  // de Fundamental y Hábito en "Distribución Fundamental" y su gráfica. `isError` gatea el botón de
  // exportar (más abajo): sin esto, un fallo del catálogo dejaría la hoja con nombres genéricos sin
  // que nadie lo note.
  const {
    data: catalogoReporteria, isLoading: catalogoCargando, isError: catalogoConError,
  } = useFundamentalsForReporting();
  const { nombreDeFundamental } = useFundamentalNaming(catalogoReporteria, lang, t);

  /**
   * T-11 — props de la sparkline de una tarjeta. Devuelve `{}` cuando no hay serie que dibujar
   * (periodo suprimido por RN-V05, o un solo día), y así la tarjeta se pinta igual que antes.
   */
  /**
   * UAT 24-ago — las 4 tarjetas de adopción. Se construyen aparte porque cada una decide si tiene
   * dato: una tarjeta sin dato muestra «—» y se queda SIN pie, en vez de publicar un porcentaje que
   * no se puede calcular.
   */
  const deSegmento = useCallback((
    etiqueta: string, dato: AdopcionDto | null | undefined, color: string,
  ): KpiItem => {
    const numero = (n: number) => n.toLocaleString(i18n.language);
    const pct = pctAdopcion(dato);
    if (pct === null) return { label: etiqueta, value: SIN_DATO, color };
    return {
      label: etiqueta,
      // Fix (2 decimales) — ver el comentario de `pctAdopcion`: `.toFixed(2)` fija el string
      // (JS recorta ceros de sobra) para que las tarjetas de Diario y Brillamos usen el mismo
      // formato de porcentaje.
      value: `${pct.toFixed(2)}%`,
      color,
      sub: t('reports.daily.adoptionHint', {
        reconocedores: numero(dato!.reconocedores),
        plantilla: numero(dato!.plantilla),
      }),
      subTone: 'muted',
    };
  }, [t, i18n.language]);

  /**
   * UAT 24-ago — «Usuarios Staff»/«Usuarios Frontline»: SÍ cambian con el Fundamental elegido, el
   * backend las calcula desde `cur` (buckets ya recortados por el filtro). No llevan aviso.
   */
  const tarjetasDeAdopcionSegmento = useMemo((): KpiItem[] => [
    deSegmento(t('reports.daily.adoptionStaff'), data?.adopcionStaff, '#342F82'),
    deSegmento(t('reports.daily.adoptionFrontline'), data?.adopcionFrontline, '#F08521'),
  ], [data, t, deSegmento]);

  /**
   * UAT 24-ago — «Usuarios totales»/«Sin actividad»: de «alguna vez», NO del periodo ni del
   * Fundamental elegido — de ahí el aviso propio (ver el `<AvisoFiltroNoAplica motivo="historico">`
   * que las precede). Separadas del resto de las 4 tarjetas de adopción (antes un solo `KpiRow`) para
   * que el aviso quede pegado A ELLAS DOS y no también a Staff/Frontline, que sí responden al filtro
   * — un aviso que dice «esto no funciona» sobre algo que sí funciona hace el mismo daño que callarlo.
   */
  const tarjetasDeAdopcionHistorica = useMemo((): KpiItem[] => {
    const numero = (n: number) => n.toLocaleString(i18n.language);
    const historica = data?.adopcionHistorica;
    const pctHistorico = pctAdopcion(historica);
    // «Sin actividad» se DERIVA de la histórica; no es un campo propio, para que no pueda
    // desincronizarse de las dos cifras de las que sale.
    const sinActividad = historica && historica.plantilla > 0
      ? Math.max(0, historica.plantilla - historica.reconocedores)
      : null;

    return [
      {
        label: t('reports.daily.adoptionTotal'),
        value: historica && historica.plantilla > 0 ? numero(historica.reconocedores) : SIN_DATO,
        sub: pctHistorico !== null
          ? t('reports.daily.adoptionTotalHint', {
              pct: `${pctHistorico.toFixed(2)}%`,
              plantilla: numero(historica!.plantilla),
            })
          : undefined,
        subTone: 'muted',
      },
      {
        label: t('reports.daily.noActivity'),
        value: sinActividad !== null ? numero(sinActividad) : SIN_DATO,
        color: '#E11F26',
        sub: sinActividad !== null ? t('reports.daily.noActivityHint') : undefined,
        subTone: 'muted',
      },
    ];
  }, [data, t, i18n.language]);

  const spark = (etiqueta: string, valorDelDia: (punto: DailyPointDto) => number) => {
    const serie = (data?.serie ?? []).map(valorDelDia);
    if (serie.length < 2) return {};
    return {
      serie,
      serieAria: t('reports.daily.sparkAria', {
        label: etiqueta, dias: serie.length, primero: serie[0], ultimo: serie[serie.length - 1],
      }),
    };
  };

  const isEmpty = !!data && data.totalPeriodo === 0 && data.totalPeriodoPrevio === 0 && data.embudo.length === 0;
  const maxFunnel = data ? Math.max(1, ...data.embudo.map((s) => s.count)) : 1;

  /**
   * Los KPI del «Resumen Ejecutivo», ya resueltos a NÚMERO (o al guion de «sin dato» cuando el
   * agregado está suprimido o el censo no respondió). Cuanto la hoja necesita saber del periodo se
   * resuelve aquí; `dailyExcel.ts` solo los pinta.
   */
  function kpisDelLibro(): KpisDiario {
    // `plantilla <= 0` es DESCONOCIDO, no «grupo vacío» (contrato de `AdopcionDto`): `pctAdopcion`
    // ya devuelve `null` en ese caso y sirve de guarda para las tres filas de este bloque.
    const historica = data!.adopcionHistorica;
    const pctHistorica = pctAdopcion(historica);

    return {
      pares: data!.pares,
      lideres: data!.lideres,
      adopcionStaff: fraccionDeAdopcion(data!.adopcionStaff),
      detalleAdopcionStaff: detalleDeAdopcion(data!.adopcionStaff, t),
      adopcionFrontline: fraccionDeAdopcion(data!.adopcionFrontline),
      detalleAdopcionFrontline: detalleDeAdopcion(data!.adopcionFrontline, t),
      usuariosTotales: pctHistorica !== null ? historica!.reconocedores : SIN_DATO,
      detalleUsuariosTotales: pctHistorica !== null
        ? t('reports.daily.adoptionTotalHint', {
            pct: `${pctHistorica.toFixed(2)}%`, plantilla: historica!.plantilla,
          })
        : '',
      // «Sin actividad» se DERIVA de las dos cifras de adopción, no viaja como campo propio: así no
      // puede desincronizarse de ellas.
      sinActividad: pctHistorica !== null
        ? Math.max(0, historica!.plantilla - historica!.reconocedores)
        : SIN_DATO,
      reconocimientosEnviados: data!.totalPeriodo,
      periodoAnterior: data!.totalPeriodoPrevio,
      // Fracción 0-1 con signo: `variacionPct` llega ya multiplicada por cien, y el formato nativo
      // de Excel (`+0.0%;-0.0%`) la pinta. Como número sigue siendo ordenable, a diferencia del
      // texto «+12.5 %» que salía antes alineado del lado contrario a los conteos de su columna.
      variacion: data!.variacionPct / 100,
      personasUnicas: data!.personasUnicas,
      plantillaTotal: historica?.plantilla ?? SIN_DATO,
      plantillaStaff: data!.adopcionStaff?.plantilla ?? SIN_DATO,
      plantillaFrontline: data!.adopcionFrontline?.plantilla ?? SIN_DATO,
    };
  }

  /**
   * Rango exacto del PERIODO (no el del embudo, que tiene su propia ventana mensual): del primer y
   * último punto de `data.serie`, que cubre EXACTAMENTE el periodo que pintan `totalPeriodo`/los
   * Top — más fiable que recalcular el rango por omisión ("semana de reference") en el cliente. La
   * serie viene vacía SOLO cuando el periodo está suprimido por el umbral de anonimato (RN-V05); ahí
   * se cae al rango EXPLÍCITO del filtro si lo hay. Sin ninguno de los dos, no hay forma honesta de
   * saber el rango exacto — la hoja "Todos los reconocimientos" documenta el hueco en vez de adivinar.
   */
  function periodoDeExportacion(): { from: string; to: string } | null {
    const serie = data?.serie ?? [];
    if (serie.length > 0) return { from: serie[0].fecha, to: serie[serie.length - 1].fecha };
    if (params.startDate && params.endDate) return { from: params.startDate, to: params.endDate };
    return null;
  }

  /*
   * Fix final (revisión de rama) — la migración xlsx→exceljs volvió async a `exportMultiSheetExcel`
   * y este handler quedó sin try/catch: un fallo (401/500/dato malformado) rechazaba la promesa SIN
   * manejar — cero mensaje, cero log, el mismo hallazgo que ya se corrigió en `handleExportShine`
   * (`ShineTab.tsx`, fix round 1). Mismo patrón aquí: estado de carga/error propio y el mensaje se
   * REUSA de `reports.shine.exporting`/`exportError` (ya genérico, sin mención a "Shine" en su
   * texto) en vez de duplicarlo — mismo criterio de reuso cross-tab que ya usa este archivo con
   * `t('reports.shine.colRecognitions')` en `buildFundamentalesSheet`/`buildEmbudoSheet`.
   */
  const [exportandoDiario, setExportandoDiario] = useState(false);
  const [exportDiarioError, setExportDiarioError] = useState(false);

  async function handleExportDaily() {
    if (!data) return;
    setExportandoDiario(true);
    setExportDiarioError(false);
    try {
      // Traductor acotado a `reports.daily`: `dailyExcel.ts` y `excelPlantillaTeal.ts` resuelven
      // ahí TODAS sus claves, así que el libro entero sale en el idioma de quien descarga.
      const traducir: Traductor = (clave, opciones) => t(`reports.daily.${clave}`, opciones ?? {});

      const resolvedTopPersonas = withResolvedNames(data.topPersonas, resolvePersonName);
      const resolvedTopReconocedores = withResolvedNames(data.topReconocedores ?? [], resolvePersonName);

      const rangoEmbudo = data.embudoDesde && data.embudoHasta
        ? `${data.embudoDesde.slice(0, 10)} – ${data.embudoHasta.slice(0, 10)}`
        : SIN_DATO;

      const hojaEmbudo = data.embudo.length > 0
        ? buildEmbudoSheet(traducir, data.embudo, traducir('excel.funnelSubtitle', { rango: rangoEmbudo }))
        : buildHojaSinDatos(
            traducir('excel.sheetFunnel'), traducir('excel.funnelTitle'), traducir('excel.funnelEmpty'),
          );

      const hojaTopReconocedores = resolvedTopReconocedores.length > 0
        ? buildRankingSheet(
            traducir,
            traducir('excel.sheetTopRecognizers'),
            traducir('excel.topRecognizersTitle'),
            filasDeRanking(resolvedTopReconocedores, traducir),
          )
        : buildHojaSinDatos(
            traducir('excel.sheetTopRecognizers'),
            traducir('excel.topRecognizersTitle'),
            traducir('excel.topRecognizersEmpty'),
          );

      // El export CRUDO paginado por RANGO DE FECHAS (el Diario no tiene campaña) alimenta «Todos
      // los reconocimientos». `truncado`: si el recorrido se cortó por el TOPE (no por página
      // vacía), el archivo es INCOMPLETO — se avisa con una fila extra en «Definiciones», nunca en
      // silencio. `periodoDeExportacion` documenta el hueco, sin inventar un rango, cuando no se
      // puede determinar el periodo exacto.
      const periodo = periodoDeExportacion();
      let hojaTodos: ExcelSheetSpec;
      let isTruncado = false;
      if (periodo) {
        const { filas, truncado } = await fetchRecognitionsCrudoPaginado(periodo.from, periodo.to);
        isTruncado = truncado;
        hojaTodos = buildTodosLosReconocimientosSheet(
          traducir,
          filas,
          // La matriz de hábitos se filtra a Fundamentales/Hábitos ACTIVOS: el backend solo emite
          // una clave por Daily Habit activo al momento del export, así que una columna fija para
          // uno retirado saldría siempre en blanco y se leería como «nadie lo marcó nunca».
          resolverFundamentalesConHabitos(catalogoReporteria, lang),
        );
      } else {
        hojaTodos = buildHojaSinDatos(
          traducir('excel.sheetAllRecognitions'),
          traducir('excel.allRecognitionsTitle'),
          traducir('excel.allRecognitionsNoRange'),
        );
      }

      const metadatos: MetadatosDiario = {
        periodo: [params.startDate, params.endDate].filter(Boolean).join(' – ') || SIN_DATO,
        filtrosAplicados: conFiltrosDeTira(
          params.ouIds?.length ? params.ouIds.map(resolveOuName).join(', ') : SIN_DATO,
          params,
          nombreDeFundamental,
          resolvePersonName,
          // Los dos rótulos de la tira también viajan traducidos: el libro entero sale en el idioma
          // de quien descarga, y dejarlos en español partiría esa línea a medio idioma.
          { fundamental: t('reports.filters.fundamental'), persona: t('reports.filters.person') },
        ),
        mesDelEmbudo: rangoEmbudo,
        generado: new Date().toLocaleString(i18n.language),
      };

      const hojasDeDetalle = [
        hojaEmbudo,
        buildRankingSheet(
          traducir,
          traducir('excel.sheetTopRecognized'),
          traducir('excel.topRecognizedTitle'),
          filasDeRanking(resolvedTopPersonas, traducir),
        ),
        hojaTopReconocedores,
        hojaTodos,
      ];

      await exportMultiSheetExcel({
        fileName: NOMBRE_ARCHIVO,
        sheets: [
          buildResumenEjecutivoSheet(traducir, metadatos, kpisDelLibro()),
          buildDefinicionesSheet(traducir, isTruncado),
          ...hojasDeDetalle,
        ],
      });

      // Fire-and-forget (mismo patrón que `auditDemograficoExport`/`auditLeadersListExport`): NUNCA
      // bloquea la descarga, que ya se completó arriba. `rowCount`: filas de DATOS (sin cabecera) de
      // las hojas de detalle, mismo criterio que `DemograficoExportButton.tsx`.
      const rowCount = hojasDeDetalle
        .reduce((total, sheet) => total + Math.max(0, sheet.rows.length - 1), 0);
      recognitionReportsApi.auditDailyExport(params.ouIds?.join(',') || params.ou, rowCount).catch((error: unknown) => {
        if (import.meta.env.DEV) console.error('No se pudo registrar la auditoría del export Diario:', error);
      });
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('No se pudo generar el export de Diario:', error);
      setExportDiarioError(true);
    } finally {
      setExportandoDiario(false);
    }
  }

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-calendar-day" aria-hidden="true" /> {t('reports.daily.title')}
          </div>
          <div className="au-card-sub">{t('reports.daily.subtitle')}</div>
        </div>
      </div>

      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        onRetry={() => q.refetch()}
      >
        {data && (
          <>
            {data.suprimido && <SuppressedNote />}

            {/* E6 — export multi-hoja del tab completo, al inicio del bloque: no depende de cuál
                sección esté visible ni de scroll para encontrarlo (mismo criterio que `DemograficoExportButton`). */}
            {canExportData && (
              <div className="rr-actions">
                <button
                  type="button"
                  className="rr-btn"
                  onClick={handleExportDaily}
                  // `catalogoCargando`/`catalogoConError` (paridad con `ShineTab.tsx`): sin esto, un
                  // clic antes de que el catálogo resuelva arma "Distribución Fundamental" con
                  // nombres genéricos y "Todos los reconocimientos" con CERO columnas de hábito, en
                  // silencio.
                  disabled={exportandoDiario || catalogoCargando || catalogoConError}
                >
                  <i
                    className={exportandoDiario ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
                    aria-hidden="true"
                  />{' '}
                  {exportandoDiario ? t('reports.shine.exporting') : t('reports.daily.exportMultiSheet')}
                </button>
              </div>
            )}

            {/* Mismo hallazgo que `handleExportShine` (fix round 1, `ShineTab.tsx`): sin esto un fallo
                del export quedaba en silencio, sin mensaje ni log. */}
            {exportDiarioError && (
              <p className="rr-state rr-state--error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.shine.exportError')}
              </p>
            )}

            <KpiRow
              items={[
                {
                  label: t('reports.daily.thisPeriod'),
                  value: data.totalPeriodo,
                  color: '#342F82',
                  /*
                   * UAT 24-ago — la comparación contra el periodo anterior va AQUÍ, no en dos
                   * tarjetas propias. Ver la nota de `KpiItem.sub`: como tarjetas hacían la fila de
                   * 6 y dejaban dos sin sparkline, que se reportó como «faltan 2 gráficos».
                   *
                   * El número del periodo anterior sigue A LA VISTA —es el dato que T-03 rescató de
                   * la supresión por ventana— solo que como línea secundaria, igual que la maqueta.
                   */
                  sub: t('reports.daily.vsPrev', {
                    variacion: formatVariation(data.variacionPct),
                    previo: data.totalPeriodoPrevio,
                  }),
                  subTone: variationTone(data.variacionPct),
                  ...spark(t('reports.daily.thisPeriod'), (p) => p.enviados),
                },
                {
                  label: t('reports.daily.uniquePeople'),
                  value: data.personasUnicas,
                  color: '#40B8E9',
                  ...spark(t('reports.daily.uniquePeople'), (p) => p.personasUnicas),
                },
                {
                  label: t('reports.daily.pares'),
                  value: data.pares,
                  color: '#20B15A',
                  ...spark(t('reports.daily.pares'), (p) => p.pares),
                },
                {
                  label: t('reports.daily.lideres'),
                  value: data.lideres,
                  color: '#F08521',
                  ...spark(t('reports.daily.lideres'), (p) => p.lideres),
                },
              ]}
            />

            {/*
              UAT 24-ago — segunda fila de la maqueta: las 4 tarjetas de adopción, que la pantalla no
              tenía. Es el hueco más visible del inventario, y está definido en la hoja «Definiciones»
              de `01 - Diario.xlsx`.

              La maqueta las construye con una ASIMETRÍA deliberada que se replica tal cual: las dos
              de segmento muestran el PORCENTAJE grande con los conteos en el pie; las dos globales
              muestran el CONTEO grande con el porcentaje en el pie. Tiene sentido — de Staff y
              Frontline interesa la proporción, y de la plantilla total interesa cuánta gente es.
            */}
            {/* Staff/Frontline SÍ responden al Fundamental elegido: fila propia, sin aviso. */}
            <KpiRow items={tarjetasDeAdopcionSegmento} />

            {/* «Usuarios totales» y «Sin actividad» son de «alguna vez», no del periodo elegido —
                el aviso va SOLO sobre esta fila, no sobre Staff/Frontline de arriba. */}
            <AvisoFiltroNoAplica filtros={filtrosActivos} motivo="historico" />
            <KpiRow items={tarjetasDeAdopcionHistorica} />

            {/*
              Distribución por Fundamental, a ANCHO COMPLETO y encima del embudo, en el mismo orden que
              la maqueta: KPIs → KPIs de adopción → Distribución → Embudo → los dos Top.
            */}
            <h3 className="au-card-title rr-block-heading">{t('reports.daily.fundamentalDistribution')}</h3>
            <DistribucionFundamental
              entradas={data.topFundamentales}
              total={data.totalPeriodo}
              habitos={data.topHabitos ?? []}
            />

            {/* Embudo de participación — E5: selector propio de año/mes + rango exacto visible */}
            {/* El embudo SÍ acepta Fundamental (reparte por él dentro de cada etapa); solo avisa de la persona. */}
            <AvisoFiltroNoAplica filtros={soloPersona} motivo="embudo" />
            <div className="rr-funnel-header">
              <h3 className="au-card-title rr-funnel-title">
                {t('reports.daily.funnel')}
              </h3>
              <div className="rr-funnel-filter">
                <label className="rr-sr-only" htmlFor="rr-funnel-month">
                  {t('reports.daily.funnelMonthSelector')}
                </label>
                <input
                  type="month"
                  id="rr-funnel-month"
                  className="rr-funnel-select"
                  value={funnelMonth}
                  onChange={(evt) => setFunnelMonth(evt.target.value)}
                />
              </div>
            </div>
            {data.embudoDesde && data.embudoHasta && (
              <p className="rr-funnel-range">
                {t('reports.daily.funnelRange', {
                  from: data.embudoDesde.slice(0, 10),
                  to: data.embudoHasta.slice(0, 10),
                })}
              </p>
            )}
            {data.embudo.length === 0 ? (
              <div className="rr-state">{t('reports.common.empty')}</div>
            ) : (
              <div className="rr-funnel">
                {data.embudo.map((stage) => {
                  /*
                   * 🔴 El ancho NO es el porcentaje de plantilla, y ADEMÁS tiene un mínimo.
                   *
                   * Primer intento (24-ago): escalar a la etapa mayor en vez de a la plantilla. No
                   * sirvió. La etapa mayor ES Inactivos, y con los datos reales de QA son 42 255
                   * contra 22 — un factor de 1 900×. Correcto y visualmente idéntico a antes: las dos
                   * etapas con gente seguían invisibles.
                   *
                   * La invariante que hay que sostener es «una etapa con gente dentro NUNCA se dibuja
                   * como cero», y para eso hace falta un suelo. Con datos muy sesgados las dos barras
                   * pequeñas quedan del mismo tamaño y es el número el que las distingue; eso es
                   * aceptable, porque a esa escala la comparación visual ya no aporta nada y lo que
                   * importa es no afirmar un cero falso.
                   */
                  const proporcional = (stage.count / maxFunnel) * 100;
                  const width = stage.count > 0 ? Math.max(ANCHO_MINIMO_ETAPA, proporcional) : 0;
                  const ancho = Math.max(0, Math.min(100, width));
                  /*
                   * 🔴 El texto sale FUERA cuando la barra es estrecha. Con datos reales de QA
                   * (42 255 inactivos contra 22 habituales) las dos barras pequeñas quedan en el suelo
                   * del 2 % y el rótulo dentro se recortaba a «<0.» — ilegible, y peor que ilegible:
                   * parecía un dato. Reporte de UAT.
                   *
                   * El umbral es del ANCHO, no del texto: medir el texto exigiría leer el DOM tras
                   * pintar, y una decisión de layout que depende de una medición asíncrona parpadea.
                   */
                  const textoDentro = ancho >= ANCHO_MINIMO_TEXTO_DENTRO;
                  const etiqueta = (
                    <>
                      <span className="rr-funnel-bar-label">{stage.etapa}</span>
                      <span className="rr-funnel-bar-value">
                        {stage.pct != null ? `${formatPctEmbudo(stage.pct)} · ` : ''}
                        {t('reports.daily.funnelPeople', { count: stage.count })}
                      </span>
                    </>
                  );

                  return (
                    <div key={stage.etapa} className="rr-funnel-row">
                      <div
                        className={textoDentro ? 'rr-funnel-bar' : 'rr-funnel-bar rr-funnel-bar--estrecha'}
                        style={{
                          width: `${ancho}%`,
                          background: COLOR_DE_ETAPA[stage.etapa] ?? COLOR_ETAPA_POR_OMISION,
                        }}
                      >
                        {textoDentro && etiqueta}
                      </div>
                      {/* Fuera de la barra, pero en la MISMA fila: se sigue leyendo como suya. */}
                      {!textoDentro && <div className="rr-funnel-fuera">{etiqueta}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tops */}
            {/*
              Los DOS Top, a media pantalla cada uno. Antes esta rejilla `auto-fit` tenía TRES hijos
              —el tercero era la distribución por Fundamental— así que en pantalla ancha se repartía en
              tres columnas y las dos tablas quedaban apretadas. Es la queja de «el diseño de las
              tablas no es igual» de las capturas de QA. La distribución subió a ancho completo, arriba.
            */}
            <div className="rr-two-col rr-two-col--pair" style={{ marginTop: 16 }}>
              {/*
                🔴 Cada Top es su propia TARJETA con su propia paginación, como en la maqueta. Antes
                eran dos tablas sueltas bajo UN control compartido, y compartir el control obligaba a
                que las dos enseñaran el mismo número de filas: al bajar por una, la otra saltaba
                también. Son dos rankings distintos y se leen por separado.

                La paginación es de CLIENTE: `top` es un único parámetro de la petición, así que se
                pide un lote una vez y cada tabla recorta el suyo. No cuesta una petición extra y las
                dos quedan independientes de verdad.
              */}
              <TopConPaginacion
                titulo={t('reports.daily.topPeople')}
                entradas={withResolvedNames(data.topPersonas, resolvePersonName)}
                colCount={t('reports.daily.colRecos')}
              />
              <TopConPaginacion
                titulo={t('reports.daily.topRecognizers')}
                entradas={withResolvedNames(data.topReconocedores ?? [], resolvePersonName)}
                colCount={t('reports.daily.colEnviados')}
              />
            </div>

          </>
        )}
      </ReportStatus>
    </div>
  );
}

/**
 * Un Top con su propia tarjeta y su propia paginación de cliente.
 *
 * Vive aquí y no en `TopPersonasTable` porque la tabla es compartida con otras pantallas que no
 * paginan; envolverla mantiene ese componente sin estado.
 */
function TopConPaginacion({
  titulo, entradas, colCount,
}: Readonly<{ titulo: string; entradas: RankedEntryDto[]; colCount: string }>) {
  const pag = usePaginacion(entradas);

  return (
    <div className="rr-top-card">
      <h3 className="au-card-title rr-subheading">{titulo}</h3>
      <TopPersonasTable entries={pag.visibles} colCount={colCount} />
      <Paginacion
        siempreVisible
        pagina={pag.pagina}
        totalPaginas={pag.totalPaginas}
        total={pag.total}
        tamano={pag.tamano}
        onPagina={pag.setPagina}
        onTamano={pag.cambiarTamano}
      />
    </div>
  );
}
